// Bozuk uretilmis gorseli duzeltme: kullanici secer -> ayni urun + sahne yeniden uretilir
// (kuyruk) -> eski/yeni yan yana -> onaylaninca galeri + taslaklar + yayindaki Etsy ilanlari
// ayni sirada guncellenir. Aday onaylanmadikca hicbir yer degismez.

import { useEffect, useRef, useState } from "react";
import { CheckCircle2, ExternalLink, Loader2, RefreshCw, X } from "lucide-react";
import { authHeaders, getBackendUrl } from "../../lib/http";

type Usage = {
  drafts: Array<{ id: string; title: string; status: string }>;
  listings: Array<{ draftId: string; title: string; shopId: string; listingId: string; rank: number; imageCount: number }>;
};
type Replacement = {
  itemId: string | null;
  status: "pending" | "processing" | "done" | "error" | "applied" | null;
  error: string | null;
  candidateUrl: string | null;
  currentUrl: string;
  usage: Usage;
};
type ApplyResult = { drafts: number; etsy: Array<{ listingId: string; title: string; ok: boolean; error?: string }> };

const abs = (u: string) => (u.startsWith("http") || u.startsWith("data:") ? u : `${getBackendUrl()}${u}`);
const base = (id: string) => `${getBackendUrl()}/api/product-shots/${id}`;

export default function ShotFixModal({ shotId, onClose, onApplied }: { shotId: string; onClose: () => void; onApplied: () => void }) {
  const [rep, setRep] = useState<Replacement | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState<ApplyResult | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  async function load() {
    const r = await fetch(`${base(shotId)}/replacement`, { headers: authHeaders() });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { setErr(j.error || "Durum alınamadı"); return; }
    setRep(j.data as Replacement);
  }

  useEffect(() => { void load(); }, [shotId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Uretim surerken 5 sn'de bir durum
  const running = rep?.status === "pending" || rep?.status === "processing";
  useEffect(() => {
    if (!running) return;
    timer.current = setInterval(() => void load(), 5000);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [running]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function regenerate() {
    setBusy(true); setErr("");
    try {
      const r = await fetch(`${base(shotId)}/regenerate`, {
        method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify({ note: note.trim() || undefined }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(j.error || "Yeniden üretim başlatılamadı"); return; }
      await load();
    } finally { setBusy(false); }
  }

  async function apply() {
    if (!rep?.itemId) return;
    setBusy(true); setErr("");
    try {
      const r = await fetch(`${base(shotId)}/replace`, {
        method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify({ itemId: rep.itemId }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(j.error || "Güncellenemedi"); return; }
      setResult(j.data as ApplyResult);
      onApplied();
      await load();
    } finally { setBusy(false); }
  }

  const usage = rep?.usage;
  const etsyCount = usage?.listings.length ?? 0;
  const draftCount = usage?.drafts.length ?? 0;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 className="text-base font-semibold text-gray-900">Bozuk görseli yeniden üret</h2>
          <button onClick={onClose} aria-label="Kapat" title="Kapat" className="rounded-lg p-1 text-gray-500 hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>

        <div className="space-y-4 overflow-y-auto p-5">
          {!rep ? (
            <p className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-5 w-5 animate-spin text-indigo-600" /> Yükleniyor…</p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <figure>
                  <figcaption className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">{rep.status === "applied" ? "Şimdiki görsel" : "Bozuk görsel"}</figcaption>
                  <img src={abs(rep.currentUrl)} alt="" className="aspect-square w-full rounded-xl border border-gray-200 object-cover" />
                </figure>
                <figure>
                  <figcaption className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Yeni görsel</figcaption>
                  {rep.candidateUrl ? (
                    <img src={abs(rep.candidateUrl)} alt="" className="aspect-square w-full rounded-xl border-2 border-indigo-300 object-cover" />
                  ) : (
                    <div className="flex aspect-square w-full items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-500">
                      {running ? <span className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin text-indigo-600" /> Üretiliyor… (1–2 dakika)</span>
                        : rep.status === "applied" ? "Yeni görsel uygulandı."
                        : "Henüz üretilmedi."}
                    </div>
                  )}
                </figure>
              </div>

              {rep.status === "error" && rep.error && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">Üretim başarısız: {rep.error}</p>}

              {!running && (
                <div>
                  <label htmlFor="shot-fix-note" className="mb-1 block text-xs font-medium text-gray-700">Ne bozuk? (isteğe bağlı, üretime eklenir)</label>
                  <input id="shot-fix-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500}
                    placeholder="Örn. sağdaki halka kelimenin sonuna kaymış; isim Amelia değil Angelina olmalı"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                </div>
              )}

              {usage && (
                <div className="rounded-xl border border-gray-200 p-4">
                  <p className="text-sm text-gray-700">
                    Onaylanınca güncellenecek yerler: <b>ürün galerisi</b>{draftCount > 0 && <>, <b>{draftCount} taslak</b></>}{etsyCount > 0 && <>, <b>{etsyCount} yayındaki Etsy ilanı</b> (aynı sırada)</>}.
                  </p>
                  {etsyCount > 0 && (
                    <ul className="mt-2 space-y-1">
                      {usage.listings.map((l) => (
                        <li key={l.listingId} className="flex items-center gap-2 text-xs text-gray-600">
                          <span className="truncate">{l.title}</span>
                          <span className="shrink-0 text-gray-500">· {l.rank}. görsel</span>
                          <a href={`https://www.etsy.com/listing/${l.listingId}`} target="_blank" rel="noreferrer" aria-label="Etsy'de aç" title="Etsy'de aç" className="shrink-0 text-indigo-600 hover:text-indigo-700"><ExternalLink className="h-3.5 w-3.5" /></a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {result && (
                <div className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-700">
                  <p className="flex items-center gap-1 font-medium"><CheckCircle2 className="h-4 w-4" /> Güncellendi: galeri{result.drafts > 0 ? `, ${result.drafts} taslak` : ""}{result.etsy.filter((e) => e.ok).length > 0 ? `, ${result.etsy.filter((e) => e.ok).length} Etsy ilanı` : ""}.</p>
                  {result.etsy.filter((e) => !e.ok).map((e) => (
                    <p key={e.listingId} className="mt-1 text-red-700">Etsy #{e.listingId} güncellenemedi: {e.error}</p>
                  ))}
                </div>
              )}
              {err && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{err}</p>}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button onClick={onClose} className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100">Kapat</button>
          {rep && !running && (
            <button onClick={() => void regenerate()} disabled={busy}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              <RefreshCw className="h-4 w-4" /> {rep.candidateUrl ? "Tekrar üret" : "Yeniden üret"}
            </button>
          )}
          {rep?.candidateUrl && (
            <button onClick={() => void apply()} disabled={busy}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Onayla ve güncelle{etsyCount > 0 ? ` (${etsyCount} Etsy ilanı)` : ""}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
